import React, { useEffect, useRef, useState } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { ArrowLeft } from "@UI/Components/Icons/ArrowLeft";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { Check } from "@UI/Components/Icons/Check";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { Text } from "@UI/Components/Text/Text";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import messages from "./messages";
import { createOnboardingStatus } from "@modules/Users/services/onboarding";
import { useSearchParams, useSetSearchParams } from "@Libs/Router/hooks/useSearchParams";

const STEP2_IMAGES: Record<string, string> = {
  fr: require("assets/images/onboarding/step2-fr.png"),
  nl: require("assets/images/onboarding/step2-nl.png"),
};

const TOTAL_STEPS = 3;

const ANIMATION_SEQUENCE = [
  { selector: ".slide-hero", delay: 0, duration: 600 },
  { selector: ".slide-text", delay: 100, duration: 520 },
] as const;

function animateSlideIn(containerEl: HTMLElement) {
  ANIMATION_SEQUENCE.forEach(({ selector, delay, duration }) => {
    const el = containerEl.querySelector<HTMLElement>(selector);
    if (!el) return;

    el.classList.remove("animate__animated", "animate__fadeIn");
    el.style.opacity = "0";

    setTimeout(() => {
      el.style.setProperty("--animate-duration", `${duration}ms`);
      el.style.animationDelay = "0ms";
      el.style.opacity = "";
      el.classList.add("animate__animated", "animate__fadeIn");

      el.addEventListener(
        "animationend",
        () => {
          el.style.opacity = "1";
          el.classList.remove("animate__animated", "animate__fadeIn");
        },
        { once: true }
      );
    }, delay);
  });
}

const superTalentFormatter = (chunks: React.ReactNode) => (
  <Text as="span" color="orange.400" fontWeight="bold">
    {chunks}
  </Text>
);

interface IStep {
  image: string;
  title: (typeof messages)[keyof typeof messages];
  body?: (typeof messages)[keyof typeof messages];
  subtext?: (typeof messages)[keyof typeof messages];
}

const steps: IStep[] = [
  {
    image: require("assets/images/onboarding/step1.png"),
    title: messages["spRecurrenceModal.step1Title"],
    body: messages["spRecurrenceModal.step1Body"],
  },
  {
    image: require("assets/images/onboarding/step2.png"),
    title: messages["spRecurrenceModal.step2Title"],
    body: messages["spRecurrenceModal.step2Body"],
  },
  {
    image: require("assets/images/onboarding/step3.png"),
    title: messages["spRecurrenceModal.step3Title"],
    body: messages["spRecurrenceModal.step3Body"],
  },
];

interface ISPRecurrenceModalProps {
  autoOpen: boolean;
}

const SWIPE_THRESHOLD = 50;

export const SPRecurrenceModal = ({ autoOpen }: ISPRecurrenceModalProps) => {
  const planNextJobModalParam = useSearchParams('plan_next_job_modal');
  const setSearchParams = useSetSearchParams();
  const { locale } = useI18n();
  const [isOpen, setIsOpen] = useState(() => autoOpen)
  const [currentStep, setCurrentStep] = useState(0);
  const slideRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  const isLastStep = currentStep === TOTAL_STEPS - 1;
  const step = {
    ...steps[currentStep],
    ...(currentStep === 1 && { image: STEP2_IMAGES[locale.split("-")[0]] ?? "/images/onboarding/step2.png" }),
  };

  useEffect(() => {
    if (planNextJobModalParam === "true") {
      setIsOpen(true);
    }
  }, [planNextJobModalParam])

  useEffect(() => {
    if (slideRef.current) animateSlideIn(slideRef.current);
  }, [currentStep]);

  const updateOnboardingStatus = async () => {
    // call api to set onboarding status for :plan_next_job
    try {
      await createOnboardingStatus('plan_next_job')
    } catch (e) {
      console.log(e)
    }
  }

  const dismiss = () => {
    setIsOpen(false);
    setCurrentStep(0)
    updateOnboardingStatus();
    setSearchParams((p) => { p.delete("plan_next_job_modal"); return p; }, { replace: true });
  };

  const handleNext = () => {
    if (isLastStep) {
      dismiss();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta > 0) {
      handleNext();
    } else {
      handleBack();
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={dismiss}
      closeButtonDisabled
      bodyPadding="0"
      size={{ base: "full", md: "lg" }}
      isCentered
    >
      <Box
        bg="#FAF6F2"
        h={{ base: "100%" }}
        display="flex"
        flexDirection="column"
        borderRadius="lg"
        overflow="hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Flex flex="none" justify="space-between" align="center" px={4} pt={4}>
          {currentStep > 0 ? (
            <Button
              variant="ghost"
              onClick={handleBack}
              p={2}
              minW="unset"
              h="auto"
              border="none"
              aria-label="Back"
              style={{ boxShadow: "none" }}
            >
              <ArrowLeft />
            </Button>
          ) : (
            <Box w="36px" />
          )}
          <Button
            variant="ghost"
            onClick={dismiss}
            p={2}
            minW="unset"
            h="auto"
            border="none"
            aria-label="Close"
            style={{ boxShadow: "none" }}
          >
            <Xmark />
          </Button>
        </Flex>

        <Box ref={slideRef} display="flex" flexDirection="column">
          <Box flex="none" className="slide-hero" px={6} py={4}>
            <Box
              borderRadius="2xl"
              w={{ base: "200px", md: "300px" }}
              h={{ base: "200px", md: "300px" }}
              mx="auto"
              style={{
                backgroundImage: `url(${step.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </Box>

          <Box px={6} pb={2}>
            <Box className="slide-text">
              <Heading as="h2" fontSize="2xl" fontWeight="bold" mb={3}>
                <FormattedMessage {...step.title} />
              </Heading>

              {step.body && (
                <Text color="gray.700" mb={3} lineHeight="tall">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <FormattedMessage {...step.body} values={{ superTalent: superTalentFormatter } as unknown as any} />
                </Text>
              )}
              {step.subtext && (
                <Text color="gray.500" fontStyle="italic" fontSize="sm" lineHeight="tall">
                  <FormattedMessage {...step.subtext} />
                </Text>
              )}
            </Box>
          </Box>
        </Box>

        <Flex flex="none" justify="space-between" align="center" px={6} pb={8} pt={4}>
          <Flex gap={2} align="center">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <Box
                key={i}
                w="8px"
                h="8px"
                borderRadius="full"
                bg={i === currentStep ? "gray.700" : "gray.300"}
                transition="background 0.2s"
              />
            ))}
          </Flex>
          <Button
            colorScheme="seaGreen"
            borderRadius="full"
            w="56px"
            h="56px"
            minW="unset"
            p={0}
            onClick={handleNext}
            aria-label={isLastStep ? "Finish" : "Next"}
          >
            {isLastStep ? <Check /> : <ArrowRight />}
          </Button>
        </Flex>
      </Box>
    </ResponsiveModal>
  );
};

SPRecurrenceModal.displayName = "SPRecurrenceModal";
